"use client";
import React, { useRef, useState, useEffect, useCallback } from "react";
import type { CSSProperties } from "react";
import * as Icon from "@phosphor-icons/react/dist/ssr";
import { ProductType } from "../../../type/ProductType";
import Product from "../Product";
import Rate from "../../../components/Other/Rate";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperClass } from 'swiper';
import { Navigation, Thumbs, Scrollbar } from "swiper/modules";
import "swiper/css/bundle";
import { useCart } from "../../../context/CartContext";
import { useModalCartContext } from "../../../context/ModalCartContext";
import { useCompare } from "../../../context/CompareContext";
import { useModalCompareContext } from "../../../context/ModalCompareContext";
import { notifyCartStorageChanged } from "../../../context/CartContext";
import { useToast } from "../../../context/ToastContext";
import { useWhatsAppChat } from "../../../context/WhatsAppChatContext";
import { shouldEnableLoop } from "../../../utils/swiperUtils";
import { getApiUrl } from "../../../config/api";
import { makeCartVariantKey } from "../../../utils/cartVariantKey";
import { displayNomUpper } from "../../../utils/displayNom";
import { getStockQuantity, isOutOfStock } from "../../../utils/productStock";
import { PREORDER_WHATSAPP_INITIAL_MESSAGE } from "../../../utils/preorderWhatsApp";
import { PriceDisplay } from "../../Other/PriceDisplay";
import { stripHtml, getProductShareImageUrl } from "../../../utils/shareProduct";
import { applyDefaultSiteSeo, applyProductPageSeo } from "../../../utils/siteSeo";
import { tiktokViewContent } from "../../../utils/tiktokPixel";
import { metaViewContent, metaAddToCart } from "../../../utils/metaPixel";
import { useNavigate } from "react-router-dom";

interface Props {
  data: Array<ProductType>;
  productId: string | number | null;
}

const formatDate = (date: any) => {
  const options = { day: "numeric", month: "long" };
  return date.toLocaleDateString("fr-FR", options);
};

/** Unified “star” section heading (star badge image + title + optional subtitle + gradient bar) */
const ProductSectionTitleStar: React.FC<{
  badge: string;
  title: string;
  subtitle?: string;
  className?: string;
}> = ({ badge, title, subtitle, className = "" }) => (
  <div className={`relative my-8 md:my-10 ${className}`}>
    <div
      className="absolute inset-x-0 top-1/2 h-px bg-gradient-to-r from-transparent via-pink/25 to-transparent"
      aria-hidden
    />
    <div className="relative mx-auto flex max-w-2xl flex-col items-center px-4 text-center">
      <span className="mb-3 inline-flex items-center gap-2 rounded-full border border-pink/20 bg-white px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-pink shadow-sm">
        <img
          src="/star-sparkle.png"
          alt=""
          width={46}
          height={43}
          className="h-3.5 w-[14px] shrink-0 object-contain"
          aria-hidden
        />
        {badge}
      </span>
      <h3 className="text-2xl font-bold tracking-tight text-gray-900 md:text-[1.65rem]">
        {title}
      </h3>
      {subtitle ? (
        <p className="mt-2 max-w-lg text-sm leading-relaxed text-secondary md:text-[15px]">
          {subtitle}
        </p>
      ) : null}
      <div
        className="mt-5 h-1 w-20 rounded-full bg-gradient-to-r from-pink/40 via-pink to-pink/40"
        aria-hidden
      />
    </div>
  </div>
);

/** Section label with selected variant (e.g. Couleurs · Bleu) */
const VariantSectionLabel: React.FC<{
  label: string;
  selected?: string;
  placeholder?: string;
  accentColor?: string;
}> = ({ label, selected, placeholder = "Choisissez", accentColor = "#ff4370" }) => (
  <div className="mt-5 flex flex-wrap items-center gap-x-2 gap-y-1">
    <span className="text-title">{label}</span>
    {selected ? (
      <span
        className="rounded-full px-2.5 py-0.5 text-sm font-semibold md:text-base"
        style={{
          color: accentColor,
          backgroundColor: `${accentColor}18`,
        }}
      >
        {selected}
      </span>
    ) : (
      <span className="text-sm italic text-secondary">{placeholder}</span>
    )}
  </div>
);

/** Returns true if the color is white or very light so we should use dark text on it. */
function isLightColor(rgb: string | undefined, colorName?: string): boolean {
  if (/blanc|white/i.test(colorName || '')) return true;
  if (!rgb) return false;
  const hex = rgb.replace(/^#/, '').replace(/^([0-9a-f])([0-9a-f])([0-9a-f])$/i, (_, r, g, b) => r + r + g + g + b + b);
  if (hex.length !== 6) return false;
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  return luminance > 220;
}

type ColorCharacteristic = {
  nom: string;
  rgb: string;
  size: number;
  pointure: number;
  code: number;
  imageid?: string;
};

function normalizeColorImageId(imageid: unknown): string | null {
  if (imageid === null || imageid === undefined) return null;
  const value = String(imageid).trim();
  if (!value || value === "0" || value.toLowerCase() === "null") return null;
  return value;
}

function getColorImagePath(imageid: unknown): string | null {
  const normalized = normalizeColorImageId(imageid);
  return normalized ? `/images/${normalized}` : null;
}

/** Merge duplicate color rows — keep imageid/rgb from any matching row */
function mergeProductColors(rawColors: unknown[]): ColorCharacteristic[] {
  const byName = new Map<string, ColorCharacteristic>();
  for (const raw of rawColors) {
    if (!raw || typeof raw !== "object") continue;
    const row = raw as Record<string, unknown>;
    const nom = String(row.nom ?? "").trim();
    if (!nom) continue;
    const imageid = normalizeColorImageId(row.imageid);
    const existing = byName.get(nom);
    if (!existing) {
      byName.set(nom, {
        nom,
        rgb: String(row.rgb ?? ""),
        size: Number(row.size ?? 0),
        pointure: Number(row.pointure ?? 0),
        code: Number(row.code ?? 0),
        imageid: imageid ?? undefined,
      });
      continue;
    }
    byName.set(nom, {
      ...existing,
      rgb: existing.rgb || String(row.rgb ?? ""),
      code: existing.code || Number(row.code ?? 0),
      imageid: existing.imageid || imageid || undefined,
    });
  }
  return Array.from(byName.values());
}

// Function to generate image URLs for a product
const generateProductImages = (productId: string | number): string[] => {
  const images: string[] = [];
  const baseUrl = `/images/`;
  
  // Add main image (e.g., "77.jpg")
  images.push(`${baseUrl}${productId}.jpg`);
  
  // Add numbered images (e.g., "77-1.jpg", "77-2.jpg", etc.)
  for (let i = 1; i <= 10; i++) {
    images.push(`${baseUrl}${productId}-${i}.jpg`);
  }
  
  return images;
};

// Function to check if an image exists
const checkImageExists = (url: string): Promise<boolean> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      console.log(`Image found: ${url}`);
      resolve(true);
    };
    img.onerror = () => {
      console.log(`Image not found: ${url}`);
      resolve(false);
    };
    img.src = url;
  });
};

// Function to get available images for a product
const getAvailableImages = async (productId: string | number): Promise<string[]> => {
  const allImages = generateProductImages(productId);
  const availableImages: string[] = [];
  
  console.log(`Checking for images:`, allImages);
  
  for (const imageUrl of allImages) {
    const exists = await checkImageExists(imageUrl);
    if (exists) {
      availableImages.push(imageUrl);
    }
  }
  
  // If no images found, return default image
  if (availableImages.length === 0) {
    console.log(`No images found for product ${productId}, using default image`);
    availableImages.push("/default.png");
  }
  
  return availableImages;
};

const Default: React.FC<Props> = ({ data, productId }) => {
  const swiperRef: any = useRef();
  const [openPopupImg, setOpenPopupImg] = useState(false);
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [activeColor, setActiveColor] = useState<string>("");
  const [activeSize, setActiveSize] = useState<string>("");
  const [activepointure, setactivepointure] = useState<string>("");
  const [productImages, setProductImages] = useState<string[]>([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  /** Avoid broken-image flash while the real JPEG is still decoding over the network */
  const [mainImageLoaded, setMainImageLoaded] = useState(false);
  const [zoomOpen, setZoomOpen] = useState(false);
  const [zoomPos, setZoomPos] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const [zoomLevel, setZoomLevel] = useState(100); // 100%, 150%, 200%, 250%
  const [showInstructions, setShowInstructions] = useState(true);
  const thumbnailColRef = useRef<HTMLDivElement>(null);
  const zoomImgRef = useRef<HTMLImageElement>(null);
  const zoomBoxRef = useRef<HTMLDivElement>(null);
  /** Skips auto WhatsApp popup if user already opened product chat from a button */
  const waProductPromptHandledRef = useRef(false);

  const [activeTab, setActiveTab] = useState<string | undefined>("description");
  const [Qtn, setQtn] = useState(1);
  const [viewersCount, setViewersCount] = useState(0);
  const [showBuyNowModal, setShowBuyNowModal] = useState(false);

  //delete from caracteristique where code <=1000

  const { addToCart, updateCart, cartState } = useCart();
  const { openModalCart, isModalOpen } = useModalCartContext();
  const { addToCompare, removeFromCompare, compareState } = useCompare();
  const { openModalCompare } = useModalCompareContext();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { openChat: openWhatsAppChat, setFloatingProductPageContext } =
    useWhatsAppChat();
  const [colors, setColors] = useState<ColorCharacteristic[]>([]);
  const [sizes, setSizes] = useState<{ nom: string,code:number }[]>([]);
  const [pointures, setpointures] = useState<{ nom: string,code:number }[]>([]);
  const [color, setColor] = useState<number>(0);
  const [colorImages, setColorImages] = useState<Record<string, string>>({});
  const [hasCharacteristics, setHasCharacteristics] = useState<boolean>(true); // Track if product has characteristics

  const [size, setSize] = useState<number>(0);
  const [pointure, setpointure] = useState<number>(0);

  const fetchFilteredSizes = useCallback(
    async (pid: string | number, colorCode: number) => {
      try {
        const response = await fetch(
          `${getApiUrl("CARACTERISTIQUE")}?code_art=${pid}&color=${colorCode}`
        );
        const data = await response.json();

        if (data.success) {
          const sizeList = data.sizes || [];
          const pointureList = data.pointures || [];
          setSizes(sizeList);
          setpointures(pointureList);
          if (sizeList.length > 0) {
            setActiveSize(sizeList[0].nom);
            setSize(sizeList[0].code);
          } else {
            setActiveSize("");
            setSize(0);
          }
          if (pointureList.length > 0) {
            setactivepointure(pointureList[0].nom);
            setpointure(pointureList[0].code);
          } else {
            setactivepointure("");
            setpointure(0);
          }
          console.log("Filtered sizes for color", colorCode, ":", data.sizes);
          console.log(
            "Filtered pointures for color",
            colorCode,
            ":",
            data.pointures
          );
        } else {
          console.error("Error fetching filtered sizes:", data.error);
        }
      } catch (error) {
        console.error("Error fetching filtered sizes:", error);
      }
    },
    []
  );

  // Scroll thumbnail column up/down
  const scrollThumbnails = (direction: 'up' | 'down') => {
    if (thumbnailColRef.current) {
      const scrollAmount = 80; // px
      if (direction === 'up') {
        thumbnailColRef.current.scrollBy({ top: -scrollAmount, behavior: 'smooth' });
      } else {
        thumbnailColRef.current.scrollBy({ top: scrollAmount, behavior: 'smooth' });
      }
    }
  };

  useEffect(() => {
    const randomViewers = Math.floor(Math.random() * (250 - 10 + 1)) + 10;
    setViewersCount(randomViewers);
  }, []);

  // Load product images when productId changes
  useEffect(() => {
    const loadImages = async () => {
      if (productId) {
        setProductImages([]);
        setSelectedImageIndex(0);
        console.log(`Loading images for product ID: ${productId}`);
        const images = await getAvailableImages(productId);
        console.log(`Found ${images.length} images for product ${productId}:`, images);
        setProductImages(images);
      }
    };
    
    loadImages();
  }, [productId]);

  useEffect(() => {
    setMainImageLoaded(false);
  }, [productImages, selectedImageIndex]);

  useEffect(() => {
    if (productImages.length === 0) return;
    setSelectedImageIndex((idx) =>
      idx >= productImages.length ? productImages.length - 1 : idx
    );
  }, [productImages]);

  useEffect(() => {
    const pm =
      data?.find((product) => Number(product.id) === Number(productId)) ??
      (data?.length ? data[0] : undefined);
    if (!pm?.id) return;

    const plain = stripHtml(String(pm.description || "")).slice(0, 500);
    const firstImg = productImages[0];
    const imageUrlFromGallery = firstImg
      ? firstImg.startsWith("http")
        ? firstImg
        : `${window.location.origin}${firstImg.startsWith("/") ? firstImg : `/${firstImg}`}`
      : null;
    const imageUrl = imageUrlFromGallery ?? getProductShareImageUrl(pm);

    applyProductPageSeo({
      productName: String(pm.name || ""),
      productDescriptionPlain: plain,
      productId: pm.id,
      imageUrl,
    });

    tiktokViewContent({
      contentId: String(pm.id),
      contentName: String(pm.name || ""),
      value: Number(pm.originPrice ?? pm.price ?? 0) || undefined,
    });
    metaViewContent({
      contentId: String(pm.id),
      contentName: String(pm.name || ""),
      value: Number(pm.originPrice ?? pm.price ?? 0) || undefined,
    });

    return () => applyDefaultSiteSeo();
  }, [data, productId, productImages]);

  useEffect(() => {
    if (zoomOpen) {
      setZoomPos({ x: 0, y: 0 });
      setZoomLevel(100); // Reset zoom level when opening
      setShowInstructions(true); // Show instructions when opening
      
      // Auto-hide instructions after 2 seconds
      const timer = setTimeout(() => {
        setShowInstructions(false);
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [zoomOpen, selectedImageIndex]);

  // Keyboard shortcuts for zoom
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!zoomOpen) return;
      
      switch (e.key) {
        case 'Escape':
          setZoomOpen(false);
          break;
        case '+':
        case '=':
          e.preventDefault();
          handleZoomIn();
          break;
        case '-':
          e.preventDefault();
          handleZoomOut();
          break;
        case '0':
          e.preventDefault();
          handleZoomReset();
          break;
        case '1':
          e.preventDefault();
          setZoomLevel(100);
          setZoomPos({ x: 0, y: 0 });
          break;
        case '2':
          e.preventDefault();
          setZoomLevel(150);
          setZoomPos({ x: 0, y: 0 });
          break;
        case '3':
          e.preventDefault();
          setZoomLevel(200);
          setZoomPos({ x: 0, y: 0 });
          break;
        case '4':
          e.preventDefault();
          setZoomLevel(250);
          setZoomPos({ x: 0, y: 0 });
          break;
      }
    };

    if (zoomOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [zoomOpen, zoomLevel]);

  let productMain = data?.find(
    (product) => Number(product.id) === Number(productId)
  ) as ProductType;

  useEffect(() => {
    const fetchCaracteristiques = async () => {
      if (productMain.id) {
        setColors([]);
        setColorImages({});
        setActiveColor("");
        setActiveSize("");
        setactivepointure("");
        setSizes([]);
        setpointures([]);
        try {
          console.log('Fetching characteristics for product:', productMain.id);
          const response = await fetch(`${getApiUrl('CARACTERISTIQUE_ALL')}?id=${productMain.id}`);
          const data = await response.json(); 
          
          console.log('API Response:', data);
          console.log('Colors from API:', data.colors);
          console.log('Response status:', response.status);
          console.log('Response ok:', response.ok);
          
          // Check if product has characteristics
          console.log('API Response colors:', data.colors);
          if (!data.colors || data.colors.length === 0) {
            console.log('Product has no characteristics, using product images as colors');
            setHasCharacteristics(false);
            setColors([]);
            setSizes([]);
            setpointures([]);
            return;
          }
          
          // Product has characteristics
          console.log('Product has characteristics, setting hasCharacteristics to true');
          setHasCharacteristics(true);
          
          const uniqueColors = mergeProductColors(data.colors);
          
          console.log('Unique colors after filtering:', uniqueColors);
          setColors(uniqueColors);
          console.log('Setting colors state to:', uniqueColors);

          const imageMap: Record<string, string> = {};
          uniqueColors.forEach((colorRow) => {
            const path = getColorImagePath(colorRow.imageid);
            const key = normalizeColorImageId(colorRow.imageid);
            if (path && key) imageMap[key] = path;
          });
          setColorImages(imageMap);

          setSizes([]);
          setpointures([]);

          const first = uniqueColors[0];
          if (first) {
            setActiveColor(first.nom);
            setColor(first.code);
            await fetchFilteredSizes(productMain.id, first.code);
            const firstColorImagePath = getColorImagePath(first.imageid);
            if (firstColorImagePath) {
              setProductImages((prev) => {
                const otherImages = prev.filter((img) => img !== firstColorImagePath);
                return [firstColorImagePath, ...otherImages];
              });
              setSelectedImageIndex(0);
            }
          }
        } catch (error) {
          console.error('Error fetching product characteristics:', error);
          // If error occurs, assume product has no characteristics
          console.log('Error occurred, setting hasCharacteristics to false');
          setHasCharacteristics(false);
          setColors([]);
          setSizes([]);
          setpointures([]);
        }
      }
    };
  
    fetchCaracteristiques();

  }, [productMain.id, fetchFilteredSizes]);

  // Keep color image paths in sync when colors update
  useEffect(() => {
    const imageMap: Record<string, string> = {};
    colors.forEach((colorRow) => {
      const path = getColorImagePath(colorRow.imageid);
      const key = normalizeColorImageId(colorRow.imageid);
      if (path && key) imageMap[key] = path;
    });
    if (Object.keys(imageMap).length > 0) {
      setColorImages((prev) => ({ ...prev, ...imageMap }));
    }
  }, [colors]);

  useEffect(() => {
    waProductPromptHandledRef.current = false;
  }, [productId]);

  /** Floating WhatsApp FAB uses same product-aware chat as « Poser une question » */
  useEffect(() => {
    if (!data?.length || productId == null || productId === "") {
      setFloatingProductPageContext(null);
      return;
    }

    let pm =
      data.find((product) => Number(product.id) === Number(productId)) ??
      null;
    if (!pm && data.length > 0) {
      pm = data[0];
    }
    if (!pm?.id) {
      setFloatingProductPageContext(null);
      return;
    }

    const productName =
      (pm.name && String(pm.name).trim()) || `Produit ${pm.id}`;
    setFloatingProductPageContext({ id: pm.id, name: productName });

    return () => setFloatingProductPageContext(null);
  }, [data, productId, setFloatingProductPageContext]);

  const AUTO_WHATSAPP_DELAY_MS = 15000;

  useEffect(() => {
    if (!data?.length || productId == null || productId === "") return;

    let pm =
      data.find((product) => Number(product.id) === Number(productId)) ??
      null;
    if (!pm && data.length > 0) {
      pm = data[0];
    }
    if (!pm?.id) return;

    const productName = (pm.name && String(pm.name).trim()) || `Produit ${pm.id}`;
    let cancelled = false;

    const timer = window.setTimeout(() => {
      if (cancelled || waProductPromptHandledRef.current) return;
      waProductPromptHandledRef.current = true;
      openWhatsAppChat({
        product: { id: pm!.id, name: productName },
      });
    }, AUTO_WHATSAPP_DELAY_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [data, productId, openWhatsAppChat]);

  // Debug hasCharacteristics state changes
  useEffect(() => {
    console.log('hasCharacteristics changed to:', hasCharacteristics);
  }, [hasCharacteristics]);
  
  

  
  if (!productMain && data?.length > 0) {
    productMain = data[0];
  }

  if (!productMain) {
    return <div>Product not found</div>;
  }

  const stockQty = getStockQuantity(productMain);
  const outOfStock = isOutOfStock(productMain);

  const handlePoserUneQuestion = () => {
    waProductPromptHandledRef.current = true;
    openWhatsAppChat({
      product: {
        id: productMain.id,
        name: productMain.name,
      },
    });
  };

  const handlePreorderViaWhatsApp = () => {
    waProductPromptHandledRef.current = true;
    openWhatsAppChat({
      product: {
        id: productMain.id,
        name: productMain.name,
      },
      initialMessage: PREORDER_WHATSAPP_INITIAL_MESSAGE,
    });
  };

  const handleIncreaseQuantity = () => {
    if (outOfStock) return;
    if (stockQty > 0 && Qtn >= stockQty) return;
    productMain.quantityPurchase += 1;
    setQtn((prv) => prv + 1);
    updateCart(
      productMain?.id,
      productMain?.quantityPurchase + 1,
      activeSize,
      activeColor
    );
  };

  const handleDecreaseQuantity = () => {
    if (productMain?.quantityPurchase > 1) {
      setQtn((prv) => prv - 1);
      productMain.quantityPurchase -= 1;
      updateCart(
        productMain?.id,
        productMain?.quantityPurchase - 1,
        activeSize,
        activeColor
      );
    }
  };

  const handleAddToCompare = () => {
    if (compareState.compareArray.length < 3) {
      if (
        compareState.compareArray.some((item) => item?.id === productMain?.id)
      ) {
        removeFromCompare(productMain?.id);
      } else {
        addToCompare(productMain);
      }
    } else {
      alert("Compare up to 3 products");
    }
    openModalCompare();
  };
  const funcSize = (s:string,c:number) => {
    setActiveSize(s);
    setSize(c);
  };

  const funcpointure = (s:string,c:number) => {
    setactivepointure(s);
    setpointure(c);
  };

  const funcColor = (s:string,c:number) => {
    setActiveColor(s);
    setColor(c);
    // Reset size and pointure when color changes
    setActiveSize("");
    setSize(0);
    setactivepointure("");
    setpointure(0);
    
    // Fetch filtered sizes and pointures for this color
    fetchFilteredSizes(productMain.id, c);
    const selectedColor = colors.find(color => color.nom === s);
    const selectedColorImagePath = getColorImagePath(selectedColor?.imageid);
    if (selectedColorImagePath) {
      setProductImages(prev => {
        const otherImages = prev.filter(img => img !== selectedColorImagePath);
        return [selectedColorImagePath, ...otherImages];
      });
      setSelectedImageIndex(0); // Set the color image as the selected image
    }
  };

  const handleSwiper = (swiper: any) => {};

  const today = new Date();
  const startDate = new Date(today);
  startDate.setDate(today.getDate() + 1);
  const endDate = new Date(today);
  endDate.setDate(today.getDate() + 3);
  const formattedStartDate = formatDate(startDate);
  const formattedEndDate = formatDate(endDate);

  const trackMetaAddToCart = (quantity: number) => {
    if (!productMain?.id) return;
    const unitPrice = Number(productMain.originPrice ?? productMain.price ?? 0);
    metaAddToCart({
      contentId: String(productMain.id),
      contentName: String(productMain.name || ""),
      quantity,
      value: unitPrice * quantity,
    });
  };

  const handleAddToCartProd = (options?: { openCartModal?: boolean }): boolean => {
    if (isOutOfStock(productMain)) {
      showToast("Ce produit est en rupture de stock.", "warning");
      return false;
    }
    // For products without characteristics, skip validation and use product images
    if (!hasCharacteristics) {
      const cartItemsJSON = localStorage.getItem("cartItems");
      const cartItems: any[] = cartItemsJSON ? JSON.parse(cartItemsJSON) : [];

      // Use the currently selected product image as the color image
      const selectedImagePath = productImages[selectedImageIndex];
      
      // Extract just the filename from the path (e.g., "/images/59-2.jpg" -> "59-2.jpg")
      const selectedImageFilename = selectedImagePath ? selectedImagePath.split('/').pop() : null;

      console.log("Adding product without characteristics to cart:", {
        productId: productMain?.id,
        selectedImagePath,
        selectedImageFilename,
        colorImageId: selectedImageFilename || null
      });

      const newCartItem = {
        id: productMain?.id, 
        quantity: Qtn,
        colorCode: 0, // No color code for products without characteristics
        sizeCode: 0, // No size code for products without characteristics
        pointureCode: 0, // No pointure code for products without characteristics
        color: "Standard", // Default color name
        pointure: "",
        size: "",
        colorImageId: selectedImageFilename || null, // Use just the filename
        livraison: productMain?.slug
      };

      if (!newCartItem.id) {
        console.error("Product ID is not defined.");
        return false;
      }

      const newVariantKey = makeCartVariantKey({
        productId: newCartItem.id,
        colorCode: newCartItem.colorCode,
        sizeCode: newCartItem.sizeCode,
        pointureCode: newCartItem.pointureCode,
      });

      const existingItemIndex = cartItems.findIndex((item) => {
        const itemVariantKey = makeCartVariantKey({
          productId: item.id,
          colorCode: item.colorCode,
          sizeCode: item.sizeCode,
          pointureCode: item.pointureCode,
        });
        return itemVariantKey === newVariantKey;
      });

      if (existingItemIndex >= 0) {
        cartItems[existingItemIndex].quantity += newCartItem.quantity;
      } else {
        cartItems.push(newCartItem);
      }
      localStorage.setItem("cartItems", JSON.stringify(cartItems));
      notifyCartStorageChanged();
      trackMetaAddToCart(newCartItem.quantity);
      if (options?.openCartModal !== false) {
        openModalCart();
      }
      return true;
    }

    // For products with characteristics, use existing validation logic
    // Check if color is required and selected
    if (colors && colors.length > 0 && !activeColor) {
      showToast("Veuillez sélectionner une couleur", "warning");
      return false;
    }
    
    // Check if size is required and selected
    if (sizes && sizes.length > 0 && !activeSize) {
      showToast("Veuillez sélectionner une taille", "warning");
      return false;
    }
    
    // Check if pointure is required and selected
    if (pointures && pointures.length > 0 && !activepointure) {
      showToast("Veuillez sélectionner une pointure", "warning");
      return false;
    }

    const cartItemsJSON = localStorage.getItem("cartItems");
    const cartItems: any[] = cartItemsJSON ? JSON.parse(cartItemsJSON) : [];

    // Get the selected color's imageid
    const selectedColor = colors.find(c => c.nom === activeColor);
    const colorImageId = selectedColor?.imageid;

    console.log("Adding product with characteristics to cart:", {
      productId: productMain?.id,
      selectedColor: selectedColor?.nom,
      colorImageId,
      activeColor,
      activeSize,
      activepointure
    });

    const newCartItem = {
      id: productMain?.id, 
      quantity: Qtn,
      colorCode: color, 
      sizeCode: size,
      pointureCode: pointure,
      color: activeColor,
      pointure: activepointure,
      size: activeSize,
      colorImageId: colorImageId,
      livraison:productMain?.slug
    };

    if (!newCartItem.id) {
      console.error("Product ID is not defined.");
      return false;
    }

    const newVariantKey = makeCartVariantKey({
      productId: newCartItem.id,
      colorCode: newCartItem.colorCode,
      sizeCode: newCartItem.sizeCode,
      pointureCode: newCartItem.pointureCode,
    });

    const existingItemIndex = cartItems.findIndex((item) => {
      const itemVariantKey = makeCartVariantKey({
        productId: item.id,
        colorCode: item.colorCode,
        sizeCode: item.sizeCode,
        pointureCode: item.pointureCode,
      });
      return itemVariantKey === newVariantKey;
    });

    if (existingItemIndex >= 0) {
      cartItems[existingItemIndex].quantity += newCartItem.quantity;
    } else {
      cartItems.push(newCartItem);
    }
    localStorage.setItem("cartItems", JSON.stringify(cartItems));
    notifyCartStorageChanged();
    trackMetaAddToCart(newCartItem.quantity);
    if (options?.openCartModal !== false) {
      openModalCart();
    }
    return true;
  };

  const validateBuyNowSelections = (): boolean => {
    if (outOfStock) return false;
    if (colors && colors.length > 0 && !activeColor) {
      showToast("Veuillez sélectionner une couleur", "warning");
      return false;
    }
    if (sizes && sizes.length > 0 && !activeSize) {
      showToast("Veuillez sélectionner une taille", "warning");
      return false;
    }
    if (pointures && pointures.length > 0 && !activepointure) {
      showToast("Veuillez sélectionner une pointure", "warning");
      return false;
    }
    return true;
  };

  const handleBuyNowClick = () => {
    if (!validateBuyNowSelections()) return;
    setShowBuyNowModal(true);
  };

  const handleBuyNowPlaceOrder = () => {
    if (!handleAddToCartProd({ openCartModal: false })) return;
    setShowBuyNowModal(false);
    navigate("/checkout");
  };

  const handleBuyNowContinueShopping = () => {
    if (!handleAddToCartProd({ openCartModal: false })) return;
    setShowBuyNowModal(false);
    showToast("Produit ajouté au panier", "success");
  };

  // Helper to constrain pan so image stays in view
  const constrainPan = (x: number, y: number, imgW: number, imgH: number, boxW: number, boxH: number, scale: number) => {
    const maxX = Math.max(0, (imgW * scale - boxW) / 2);
    const maxY = Math.max(0, (imgH * scale - boxH) / 2);
    return {
      x: Math.max(-maxX, Math.min(maxX, x)),
      y: Math.max(-maxY, Math.min(maxY, y)),
    };
  };

  // Update handleZoomMouseMove to constrain pan
  const handleZoomMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (dragging && dragStart && zoomImgRef.current && zoomBoxRef.current) {
      const imgW = zoomImgRef.current.naturalWidth;
      const imgH = zoomImgRef.current.naturalHeight;
      const boxW = zoomBoxRef.current.offsetWidth;
      const boxH = zoomBoxRef.current.offsetHeight;
      const scale = zoomLevel / 100;
      const rawX = e.clientX - dragStart.x;
      const rawY = e.clientY - dragStart.y;
      const { x, y } = constrainPan(rawX, rawY, imgW, imgH, boxW, boxH, scale);
      setZoomPos({ x, y });
    }
  };

  // Handlers for zoom modal drag
  const handleZoomMouseDown = (e: React.MouseEvent<HTMLImageElement>) => {
    setDragging(true);
    setDragStart({ x: e.clientX - zoomPos.x, y: e.clientY - zoomPos.y });
  };
  const handleZoomMouseUp = () => {
    setDragging(false);
    setDragStart(null);
  };

  // Zoom level control functions
  const handleZoomIn = () => {
    const zoomLevels = [100, 150, 200, 250];
    const currentIndex = zoomLevels.indexOf(zoomLevel);
    const nextIndex = Math.min(currentIndex + 1, zoomLevels.length - 1);
    setZoomLevel(zoomLevels[nextIndex]);
    setZoomPos({ x: 0, y: 0 }); // Reset position when zooming
  };

  const handleZoomOut = () => {
    const zoomLevels = [100, 150, 200, 250];
    const currentIndex = zoomLevels.indexOf(zoomLevel);
    const prevIndex = Math.max(currentIndex - 1, 0);
    setZoomLevel(zoomLevels[prevIndex]);
    setZoomPos({ x: 0, y: 0 }); // Reset position when zooming
  };

  const handleZoomReset = () => {
    setZoomLevel(100);
    setZoomPos({ x: 0, y: 0 });
  };

  // Mouse wheel zoom
  const handleWheel = (e: React.WheelEvent) => {
    if (!zoomOpen) return;
    
    e.preventDefault();
    if (e.deltaY < 0) {
      handleZoomIn();
    } else {
      handleZoomOut();
    }
  };

  const mainImageSafeIndex =
    productImages.length === 0
      ? 0
      : Math.min(selectedImageIndex, productImages.length - 1);
  const mainImageSrc =
    productImages.length > 0 ? productImages[mainImageSafeIndex] ?? null : null;

  return (
    <>
      {/* Custom CSS for attention-grabbing animation */}
      <style>
        {`
          @keyframes attentionPulse {
            0%, 100% {
              box-shadow: 0 0 0 0 rgba(235, 30, 133, 0.7);
              transform: scale(1);
            }
            50% {
              box-shadow: 0 0 0 20px rgba(235, 30, 133, 0);
              transform: scale(1.02);
            }
          }
          
          @keyframes shimmer {
            0% {
              transform: translateX(-100%);
            }
            100% {
              transform: translateX(100%);
            }
          }
          
          .attention-button {
            animation: attentionPulse 2s infinite;
          }
          
          .shimmer-effect {
            animation: shimmer 2s infinite;
          }
        `}
      </style>
      
      <div className="product-detail default">
        <div className="featured-product underwear md:py-20 py-10">
          <div className="container flex justify-between gap-y-6 flex-wrap">
            <div className="fx-reveal list-img md:w-1/2 md:pr-[45px] w-full">
              <div className="w-full flex flex-col md:flex-row items-start justify-center gap-6">
                {/* Main Image */}
                <div
                  className={`flex items-center justify-center bg-[#f5f7fa] w-full max-w-[500px] min-h-[500px] min-w-[300px] relative group rounded-xl border border-pink/20 shadow-[0_8px_24px_rgba(255,67,112,0.08)] ${
                    mainImageSrc ? "cursor-zoom-in" : "cursor-default"
                  }`}
                  style={{ flex: 1 }}
                  onClick={() => mainImageSrc && setZoomOpen(true)}
                >
                  {!mainImageSrc && (
                    <div
                      className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-8"
                      aria-busy="true"
                      aria-label="Chargement de l’image"
                    >
                      <div className="h-[65%] min-h-[220px] w-full max-w-[320px] animate-pulse rounded-xl bg-gradient-to-b from-gray-200/95 to-gray-100/85" />
                      <div className="h-3 w-36 animate-pulse rounded-full bg-gray-200/80" />
                    </div>
                  )}
                  {mainImageSrc && (
                    <>
                      {!mainImageLoaded && (
                        <div
                          className="absolute inset-0 z-[1] flex flex-col items-center justify-center gap-4 bg-[#f5f7fa] p-8"
                          aria-hidden
                        >
                          <div className="h-[65%] min-h-[220px] w-full max-w-[320px] animate-pulse rounded-xl bg-gray-200/85" />
                        </div>
                      )}
                      <img
                        key={mainImageSrc}
                        src={mainImageSrc}
                        alt={`${String(productMain?.name ?? "product")}-main-${mainImageSafeIndex}`}
                        className={`relative z-[2] object-cover w-full h-full transition-all duration-300 group-hover:scale-105 ${
                          mainImageLoaded ? "opacity-100" : "opacity-0"
                        }`}
                        style={{ margin: "auto", maxHeight: "100%", maxWidth: "100%" }}
                        decoding="async"
                        onLoad={() => setMainImageLoaded(true)}
                        onError={(e) => {
                          e.currentTarget.src = "/default.png";
                          setMainImageLoaded(true);
                        }}
                      />
                    </>
                  )}
                  {/* Zoom overlay */}
                  {mainImageSrc && (
                  <div className="pointer-events-none absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-300 flex items-center justify-center z-[3]">
                    <div className="bg-white bg-opacity-90 rounded-full p-3 shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-75 group-hover:scale-100">
                      <Icon.MagnifyingGlassPlus size={24} className="text-gray-700" />
                    </div>
                  </div>
                  )}
                  {outOfStock && (
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[10] bg-gradient-to-t from-black/85 via-black/65 to-transparent px-3 pb-3 pt-10 text-center md:px-4 md:pb-4 md:pt-12">
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-white/25 bg-white/15 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide text-white backdrop-blur-sm md:text-xs">
                        <Icon.Package size={16} weight="duotone" className="opacity-90" />
                        Rupture · Précommande WhatsApp
                      </span>
                    </div>
                  )}
                </div>
                {/* Thumbnails: vertical on desktop, horizontal on mobile */}
                <div className="relative md:flex flex-col items-center justify-center w-full md:w-auto mt-4 md:mt-0">
                  {/* Desktop: vertical thumbnails with scroll buttons */}
                  <div className="hidden md:flex flex-col items-center">
                    <button
                      className="mb-2 bg-white rounded-full shadow p-1 flex items-center justify-center border border-line"
                      onClick={() => scrollThumbnails('up')}
                      aria-label="Previous thumbnails"
                      type="button"
                    >
                      <Icon.CaretUp size={24} />
                    </button>
                    <div
                      ref={thumbnailColRef}
                      className="flex flex-col gap-4 overflow-y-auto py-2 scrollbar-hide"
                      style={{ maxHeight: '400px', minHeight: '200px' }}
                    >
                      {productImages.map((image, index) => (
                        <div
                          key={index}
                          className={`w-20 h-20 min-w-[5rem] min-h-[5rem] flex items-center justify-center rounded-lg cursor-pointer transition-all duration-200 bg-[#f5f7fa] ${selectedImageIndex === index ? 'border-2 border-pink shadow-[0_0_0_2px_rgba(255,67,112,0.12)]' : 'border border-pink/20'}`}
                          onClick={() => {
                            setSelectedImageIndex(index);
                            // Check if this image corresponds to a color and select it
                            const matchingColor = colors.find(color => 
                              getColorImagePath(color.imageid) === image
                            );
                            if (matchingColor) {
                              funcColor(matchingColor.nom, matchingColor.code);
                            }
                          }}
                          style={{ boxSizing: 'border-box' }}
                        >
                          <img
                            src={image}
                            alt={`thumbnail-${index}`}
                            className="object-contain w-16 h-16"
                            onError={e => { e.currentTarget.src = '/default.png'; }}
                          />
                        </div>
                      ))}
                    </div>
                    <button
                      className="mt-2 bg-white rounded-full shadow p-1 flex items-center justify-center border border-line"
                      onClick={() => scrollThumbnails('down')}
                      aria-label="Next thumbnails"
                      type="button"
                    >
                      <Icon.CaretDown size={24} />
                    </button>
                  </div>
                  {/* Mobile: horizontal thumbnails below main image */}
                  <div className="flex md:hidden flex-row gap-2 overflow-x-auto py-2 scrollbar-hide w-full justify-center">
                    {productImages.map((image, index) => (
                      <div
                        key={index}
                        className={`w-20 h-20 min-w-[5rem] min-h-[5rem] flex items-center justify-center rounded-lg cursor-pointer transition-all duration-200 bg-[#f5f7fa] ${selectedImageIndex === index ? 'border-2 border-pink shadow-[0_0_0_2px_rgba(255,67,112,0.12)]' : 'border border-pink/20'}`}
                        onClick={() => {
                          setSelectedImageIndex(index);
                          // Check if this image corresponds to a color and select it
                          const matchingColor = colors.find(color =>
                            getColorImagePath(color.imageid) === image
                          );
                          if (matchingColor) {
                            funcColor(matchingColor.nom, matchingColor.code);
                          }
                        }}
                        style={{ boxSizing: 'border-box' }}
                      >
                        <img
                          src={image}
                          alt={`thumbnail-${index}`}
                          className="object-contain w-16 h-16"
                          onError={e => { e.currentTarget.src = '/default.png'; }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="fx-reveal product-infor md:w-1/2 w-full lg:pl-[15px] md:pl-2" style={{ "--fx-delay": "80ms" } as CSSProperties}>
              <div>
                <div className="caption2 text-secondary font-semibold uppercase">
                  {productMain?.type}
                </div>
                <div className="heading4 mt-1" style={{ color: '#ff4370' }}>{productMain?.name}</div>
              </div>
              <div className="flex items-center gap-3 flex-wrap mt-5 pb-6 border-b border-line">
                <div className="product-price heading5">
                  <PriceDisplay amount={productMain?.originPrice} size="xl" />
                </div>
                <div className="w-px h-4 bg-line"></div>
                <div className="product-origin-price font-normal text-secondary2">
                  <del>
                    <PriceDisplay
                      amount={
                        productMain?.originPrice != null
                          ? Number(productMain.originPrice) * 1.1
                          : 0
                      }
                      size="md"
                    />
                  </del>
                </div>

                {outOfStock && (
                  <div className="w-full mt-4 overflow-hidden rounded-2xl border border-amber-200/90 bg-gradient-to-br from-amber-50 via-white to-orange-50/90 shadow-[0_10px_40px_-18px_rgba(245,158,11,0.45)] ring-1 ring-amber-100/80">
                    <div className="flex flex-col gap-4 p-4 sm:p-5 md:flex-row md:items-stretch md:justify-between md:gap-6">
                      <div className="flex min-w-0 gap-3 sm:gap-4">
                        <span
                          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-amber-100 text-amber-900 shadow-inner sm:h-14 sm:w-14"
                          aria-hidden
                        >
                          <Icon.Package size={26} weight="duotone" />
                        </span>
                        <div className="min-w-0 pt-0.5">
                          <p className="flex flex-wrap items-center gap-2 text-[15px] font-bold tracking-tight text-amber-950 sm:text-base">
                            <span>Rupture de stock</span>
                            <span className="rounded-full bg-amber-200/80 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-950">
                              Précommande possible
                            </span>
                          </p>
                          <p className="mt-2 text-xs leading-relaxed text-amber-950/85 sm:text-sm">
                            Ce modèle n&apos;est pas disponible tout de suite. Écrivez-nous sur WhatsApp pour une{" "}
                            <strong className="font-semibold text-amber-950">précommande</strong> ou pour être prévenu(e) du retour en stock.
                          </p>
                        </div>
                      </div>
                      <div className="flex shrink-0 flex-col justify-center gap-2 md:w-auto md:min-w-[220px]">
                        <button
                          type="button"
                          onClick={handlePreorderViaWhatsApp}
                          className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#25D366] px-4 py-3 text-sm font-semibold text-white shadow-md transition hover:bg-[#20bd5a] hover:shadow-lg active:scale-[0.99] sm:py-3.5 sm:text-[15px]"
                        >
                          <Icon.WhatsappLogo size={22} weight="fill" className="shrink-0" />
                          Précommander via WhatsApp
                        </button>
                        <p className="text-center text-[11px] leading-snug text-amber-900/70 md:text-left">
                          Réponse rapide · même jour en général
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="w-full mt-4">
                  <div className="inline-block px-4 py-1 rounded-md mb-2" style={{ background: '#ffecf6' }}>
                    <span className="text-pink font-semibold">Description</span>
                  </div>
                <div className="desc text-secondary mt-3">
                    {productMain?.description && productMain.description.trim() !== ''
                      ? productMain.description
                      : "Aucune description n'est disponible pour ce produit pour le moment."}
                  </div>
                </div>
              </div>
              {/* Colors Section */}
              {colors && colors.length > 0 && (
                <>
                  <VariantSectionLabel
                    label="Couleurs:"
                    selected={activeColor || undefined}
                    accentColor={colors.find((c) => c.nom === activeColor)?.rgb || "#ff4370"}
                  />
                  <div className="mt-3 flex w-full flex-wrap items-start gap-3">
                    {colors.map((c, i) => {
                      const isSelected = c.nom === activeColor;
                      const swatchBorder = c.rgb || "#ff4370";
                      const colorImagePath = getColorImagePath(c.imageid);
                      const colorImageKey = normalizeColorImageId(c.imageid);
                      const selectedShadow = `0 4px 14px ${swatchBorder}55`;

                      return (
                        <div
                          key={i}
                          className="flex w-[4.5rem] flex-col items-center rounded-xl p-1 transition-all duration-200"
                          style={
                            isSelected
                              ? { backgroundColor: `${swatchBorder}18` }
                              : undefined
                          }
                        >
                          <div className="relative group">
                            <button
                              type="button"
                              onClick={() => funcColor(c.nom, c.code)}
                              className={`relative block touch-manipulation rounded-lg transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-pink/50 ${
                                isSelected
                                  ? "scale-105"
                                  : "opacity-80 hover:scale-[1.02] hover:opacity-100"
                              }`}
                              aria-label={c.nom}
                              aria-pressed={isSelected}
                            >
                              {colorImagePath ? (
                                <img
                                  src={
                                    (colorImageKey && colorImages[colorImageKey]) ||
                                    colorImagePath
                                  }
                                  alt={c.nom}
                                  className={`h-16 w-16 rounded-lg object-cover transition-all duration-200 ${
                                    isSelected ? "border-[3px]" : "border-2"
                                  }`}
                                  style={{
                                    borderColor: swatchBorder,
                                    boxShadow: isSelected
                                      ? selectedShadow
                                      : "0 1px 4px rgba(0, 0, 0, 0.08)",
                                  }}
                                  onError={(e) => {
                                    e.currentTarget.src = "/default.png";
                                  }}
                                />
                              ) : (
                                <span
                                  className={`flex h-16 w-16 items-center justify-center rounded-lg bg-white transition-all duration-200 ${
                                    isSelected ? "border-[3px]" : "border-2"
                                  }`}
                                  style={{
                                    borderColor: swatchBorder,
                                    boxShadow: isSelected
                                      ? selectedShadow
                                      : "0 1px 4px rgba(0, 0, 0, 0.06)",
                                  }}
                                  aria-hidden
                                >
                                  {c.rgb ? (
                                    <span
                                      className={`rounded-full border-[3px] border-white shadow-sm ring-1 ring-black/10 transition-transform duration-200 ${
                                        isSelected ? "h-11 w-11" : "h-10 w-10"
                                      }`}
                                      style={{ backgroundColor: c.rgb }}
                                    />
                                  ) : (
                                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 ring-1 ring-gray-200">
                                      <Icon.Palette size={20} weight="duotone" className="text-pink/70" />
                                    </span>
                                  )}
                                </span>
                              )}
                            </button>
                            {isSelected && (
                              <div
                                className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full shadow-md ring-2 ring-white"
                                style={{ backgroundColor: swatchBorder }}
                              >
                                <Icon.Check
                                  size={12}
                                  weight="bold"
                                  className={isLightColor(c.rgb, c.nom) ? "text-gray-800" : "text-white"}
                                />
                              </div>
                            )}
                            <div className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 hidden -translate-x-1/2 whitespace-nowrap rounded bg-black px-2 py-1 text-xs text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100 md:block">
                              {c.nom}
                              <div className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-black" />
                            </div>
                          </div>
                          <span
                            className={`mt-1 w-full text-center text-[11px] leading-tight line-clamp-2 transition-all duration-200 ${
                              isSelected ? "font-bold" : "text-gray-700"
                            }`}
                            style={isSelected ? { color: swatchBorder } : undefined}
                          >
                            {c.nom}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {/* Product Images as Colors Section - For products without characteristics */}
              {(!hasCharacteristics && productImages.length > 0) && (
                <>
                  <div className="text-title mt-5">Variantes:</div>
                  <div className="flex flex-wrap items-center gap-3 mt-3">
                    {productImages.map((image, i) => (
                      <div key={i} className="flex flex-col items-center">
                        <div className="relative group">
                          <img
                            src={image}
                            alt={`Variante ${i + 1}`}
                            className="w-16 h-16 object-cover rounded-lg border-2 transition-all duration-300 cursor-pointer"
                            style={{
                              borderColor: '#ff4370',
                              boxShadow: i === selectedImageIndex 
                                ? `0 0 0 2px #ff4370, 0 4px 8px rgba(255, 67, 112, 0.3)` 
                                : `0 0 0 2px #ff4370`
                            }}
                            onClick={() => {
                              setSelectedImageIndex(i);
                            }}
                            onError={(e) => {
                              e.currentTarget.src = "/default.png";
                            }}
                          />
                          
                          {/* Selection indicator */}
                          {i === selectedImageIndex && (
                            <div className="absolute -top-1 -right-1 w-4 h-4 bg-pink rounded-full flex items-center justify-center">
                              <div className="w-2 h-2 bg-white rounded-full" />
                            </div>
                          )}
                          
                          {/* Tooltip - Hidden on mobile for better UX */}
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10 hidden md:block">
                            Variante {i + 1}
                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-black"></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Sizes Section */}
              {hasCharacteristics && activeColor && sizes && sizes.length > 0 && (
                <>
                  <VariantSectionLabel
                    label="Tailles:"
                    selected={activeSize ? displayNomUpper(activeSize) : undefined}
                    accentColor={colors.find((c) => c.nom === activeColor)?.rgb || "#ff4370"}
                  />
                  <div className="mt-3 flex w-full flex-wrap items-center gap-3">
                    {sizes.map((s, i) => {
                      const selectedColor = colors.find((c) => c.nom === activeColor);
                      const useDarkText = isLightColor(selectedColor?.rgb, selectedColor?.nom);
                      const textClass = useDarkText ? "text-gray-800" : "text-white";
                      const hoverTextClass = useDarkText ? "hover:text-gray-800" : "hover:text-white";
                      return (
                        <button
                          key={i}
                          type="button"
                          onClick={() => funcSize(s.nom, s.code)}
                          className={`min-h-[44px] touch-manipulation rounded-lg border px-3 py-2 text-lg font-medium transition-all duration-300 md:px-4 md:text-xl ${
                            s.nom === activeSize ? textClass : hoverTextClass
                          }`}
                          style={{
                            borderColor: selectedColor?.rgb || "#e5e7eb",
                            backgroundColor:
                              s.nom === activeSize ? selectedColor?.rgb || "#ff4370" : "transparent",
                            boxShadow:
                              s.nom === activeSize ? `0 4px 8px ${selectedColor?.rgb || "#ff4370"}40` : "none",
                          }}
                          onMouseEnter={(e) => {
                            if (s.nom !== activeSize) {
                              e.currentTarget.style.backgroundColor = selectedColor?.rgb || "#ff4370";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (s.nom !== activeSize) {
                              e.currentTarget.style.backgroundColor = "transparent";
                            }
                          }}
                        >
                          {displayNomUpper(s.nom)}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}

              {/* Pointures Section */}
              {hasCharacteristics && activeColor && pointures && pointures.length > 0 && (
                <>
                  <VariantSectionLabel
                    label="Pointures:"
                    selected={activepointure ? displayNomUpper(activepointure) : undefined}
                    accentColor={colors.find((c) => c.nom === activeColor)?.rgb || "#ff4370"}
                  />
                  <div className="mt-3 flex w-full flex-wrap items-center gap-3">
                    {pointures.map((s, i) => {
                      const selectedColor = colors.find((c) => c.nom === activeColor);
                      const useDarkText = isLightColor(selectedColor?.rgb, selectedColor?.nom);
                      const textClass = useDarkText ? "text-gray-800" : "text-white";
                      const hoverTextClass = useDarkText ? "hover:text-gray-800" : "hover:text-white";
                      return (
                        <button
                          key={i}
                          type="button"
                          onClick={() => funcpointure(s.nom, s.code)}
                          className={`min-h-[44px] touch-manipulation rounded-lg border px-3 py-2 text-lg font-medium transition-all duration-300 md:px-4 md:text-xl ${
                            s.nom === activepointure ? textClass : hoverTextClass
                          }`}
                          style={{
                            borderColor: selectedColor?.rgb || "#e5e7eb",
                            backgroundColor:
                              s.nom === activepointure ? selectedColor?.rgb || "#ff4370" : "transparent",
                            boxShadow:
                              s.nom === activepointure
                                ? `0 4px 8px ${selectedColor?.rgb || "#ff4370"}40`
                                : "none",
                          }}
                          onMouseEnter={(e) => {
                            if (s.nom !== activepointure) {
                              e.currentTarget.style.backgroundColor = selectedColor?.rgb || "#ff4370";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (s.nom !== activepointure) {
                              e.currentTarget.style.backgroundColor = "transparent";
                            }
                          }}
                        >
                          {displayNomUpper(s.nom)}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}

              <div className="list-action mt-6">
                <div className="mt-5 text-title font-semibold text-gray-900">Quantité:</div>
                <div className="choose-quantity mt-3 flex flex-wrap items-center gap-3">
                  <div className="quantity-block flex h-12 w-[170px] flex-shrink-0 items-center justify-between rounded-xl border border-pink/20 bg-white px-2 shadow-sm">
                    <Icon.Minus
                      size={18}
                      onClick={handleDecreaseQuantity}
                      className={`${
                        productMain?.quantityPurchase === 1 ? "pointer-events-none opacity-40" : "hover:bg-pink/10"
                      } cursor-pointer touch-manipulation rounded-lg min-h-[36px] min-w-[36px] flex items-center justify-center text-pink transition-colors`}
                    />
                    <div className="text-base font-bold text-pink tabular-nums">{outOfStock ? 0 : Qtn}</div>
                    <Icon.Plus
                      size={18}
                      onClick={handleIncreaseQuantity}
                      className={`${
                        outOfStock || (stockQty > 0 && Qtn >= stockQty)
                          ? "pointer-events-none opacity-40"
                          : "cursor-pointer hover:bg-pink/10"
                      } touch-manipulation rounded-lg min-h-[36px] min-w-[36px] flex items-center justify-center text-pink transition-colors`}
                    />
                  </div>
                  <div onClick={async () => {
                    if (outOfStock) return;
                    // Check if color is required and selected
                    if (colors && colors.length > 0 && !activeColor) {
                      showToast("Veuillez sélectionner une couleur", "warning");
                      return;
                    }
                    
                    // Check if size is required and selected
                    if (sizes && sizes.length > 0 && !activeSize) {
                      showToast("Veuillez sélectionner une taille", "warning");
                      return;
                    }
                    
                    // Check if pointure is required and selected
                    if (pointures && pointures.length > 0 && !activepointure) {
                      showToast("Veuillez sélectionner une pointure", "warning");
                      return;
                    }
                    
                    await handleAddToCartProd();
                  }} className={`group/addcart relative inline-flex h-12 min-h-12 w-full min-w-[220px] flex-1 items-center justify-center gap-2 overflow-hidden rounded-xl border-2 px-4 text-center text-sm font-semibold uppercase tracking-wide shadow-sm transition-[background-color,box-shadow] duration-300 ease-out active:scale-[0.99] ${
                    outOfStock
                      ? "cursor-not-allowed border-neutral-200 bg-neutral-100 text-neutral-400 opacity-70 pointer-events-none"
                      : "cursor-pointer border-pink bg-white text-pink hover:bg-pink/[0.08] hover:shadow-[0_6px_18px_-10px_rgba(255,67,112,0.38)]"
                  }`}>
                    <Icon.ShoppingCartSimple size={18} weight="bold" className="shrink-0 transition-transform duration-300 group-hover/addcart:scale-110" />
                    <span className="max-w-[220px] overflow-hidden truncate transition-[max-width,opacity] duration-300 ease-out group-hover/addcart:pointer-events-none group-hover/addcart:max-w-0 group-hover/addcart:opacity-0">
                      Ajouter au panier
                    </span>
                  </div>
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddToCompare()}}
                    className="compare-btn button-main !text-pink !bg-white flex items-center justify-center border border-pink/25 cursor-pointer rounded-xl duration-300 hover:!bg-pink/10 hover:border-pink min-h-[48px] min-w-[48px] shadow-sm">
                    <Icon.ArrowsCounterClockwise className="heading6" />
                  </div>
                </div>
                {/* Desktop Button Block */}
                <div className="md:block button-block hidden mt-5 " style={{zIndex:200}}>
                  <div className="flex gap-3">
                  <div
                    onClick={handleBuyNowClick}
                    className={`button-main flex-1 text-center transition-all duration-200 flex items-center justify-center gap-2 ${
                      outOfStock ? "cursor-not-allowed bg-neutral-300 text-neutral-600 opacity-70 pointer-events-none" : "bg-pink hover:bg-green-600 hover:text-black"
                    }`}>
                      <Icon.CreditCard size={20} />
                    Achetez-le maintenant
                  </div>
                    <button
                      type="button"
                      onClick={handlePoserUneQuestion}
                      className="button-main px-6 text-center transition-all duration-200 bg-green-500 hover:bg-green-600 text-white flex items-center justify-center gap-2 hidden md:flex"
                    >
                      <Icon.WhatsappLogo size={20} />
                      Poser une question
                    </button>
                  </div>
                </div>

                {/* Mobile Button Block - Hidden since buttons are fixed at bottom */}
                <div className="hidden md:hidden button-block mt-5" style={{zIndex:200}}>
                  <div className="flex flex-col gap-3">
                    <div
                      onClick={handleBuyNowClick}
                      className={`button-main w-full text-center transition-all duration-200 flex items-center justify-center gap-2 py-3 ${
                        outOfStock ? "cursor-not-allowed bg-neutral-300 text-neutral-600 opacity-70 pointer-events-none" : "bg-pink hover:bg-green-600 hover:text-black"
                      }`}>
                      <Icon.CreditCard size={20} />
                      Achetez-le maintenant
                    </div>
                    <button
                      type="button"
                      onClick={handlePoserUneQuestion}
                      className="button-main w-full text-center transition-all duration-200 bg-green-500 hover:bg-green-600 text-white flex items-center justify-center gap-2 py-3"
                    >
                      <Icon.WhatsappLogo size={20} />
                      Poser une question
                    </button>
                  </div>
                </div>

                <div className="mt-6 flex flex-col gap-4 border-t border-line pt-5">
                  <div className="relative overflow-hidden rounded-xl border border-pink/15 bg-gradient-to-br from-white via-[#fefcfd] to-[#f7f5f6] p-3 shadow-[0_6px_24px_-12px_rgba(0,0,0,0.1)] md:p-4">
                    <div
                      className="pointer-events-none absolute -right-10 -top-10 h-24 w-24 rounded-full opacity-[0.1]"
                      style={{ background: "radial-gradient(circle, #ff4370 0%, transparent 72%)" }}
                      aria-hidden
                    />
                    <div className="relative flex flex-col gap-2">
                      <a
                        href="/pages/livraison-et-retour"
                        className="group flex items-center justify-between gap-2 rounded-lg border border-pink/20 bg-white/95 px-3 py-2 shadow-sm transition hover:border-pink/40 hover:shadow-md"
                      >
                        <span className="flex min-w-0 items-center gap-2.5">
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-pink/10 text-pink transition group-hover:bg-pink/15">
                            <Icon.ArrowClockwise className="text-lg" weight="bold" />
                          </span>
                          <span className="text-title font-semibold text-gray-900">Livraison et retour</span>
                        </span>
                        <Icon.CaretRight
                          className="shrink-0 text-secondary transition group-hover:translate-x-0.5 group-hover:text-pink"
                          size={18}
                          weight="bold"
                        />
                      </a>
                      <div className="grid gap-2 sm:grid-cols-2">
                        <div className="flex gap-2 rounded-lg border border-pink/15 bg-white/95 p-2.5 shadow-sm">
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-50">
                            <Icon.Timer className="text-lg text-amber-600" weight="bold" />
                          </span>
                          <div className="min-w-0 leading-tight">
                            <div className="text-[10px] font-semibold uppercase tracking-wide text-secondary">
                              Livraison estimée
                            </div>
                            <div className="mt-0.5 text-xs font-semibold text-gray-900">
                              {formattedStartDate} – {formattedEndDate}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2 rounded-lg border border-pink/15 bg-white/95 p-2.5 shadow-sm">
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-sky-50">
                            <Icon.Eye className="text-lg text-sky-600" weight="bold" />
                          </span>
                          <div className="min-w-0 leading-tight">
                            <div className="text-[10px] font-semibold uppercase tracking-wide text-secondary">
                              Activité en direct
                            </div>
                            <div className="mt-0.5 text-xs font-semibold text-gray-900">
                              <span className="tabular-nums">{viewersCount}</span>{" "}
                              <span className="font-normal text-secondary">sur cette page</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-pink/15 bg-white/95 px-3 py-2 shadow-sm">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-violet-50">
                          <Icon.Tag className="text-lg text-violet-600" weight="bold" />
                        </span>
                        <div className="min-w-0 flex-1 leading-tight">
                          <div className="text-[10px] font-semibold uppercase tracking-wide text-secondary">
                            Catégories
                          </div>
                          <div className="text-xs font-medium text-gray-900">
                            {productMain?.category}, {productMain?.gender}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Obtenez-le + Paiement sécurisé — même hauteur (stretch) sur desktop */}
            <div className="mt-8 flex w-full flex-col gap-6 border-t border-line pt-8 md:mt-10 md:flex-row md:items-stretch md:gap-y-6 md:gap-x-0">
              <div className="flex w-full min-w-0 flex-col md:w-1/2 md:max-w-none md:flex-none md:pr-[45px]">
                <div className="relative overflow-hidden rounded-2xl border border-pink/15 bg-gradient-to-br from-white via-[#fafafa] to-[#f5f5f5] p-3 shadow-[0_12px_36px_-18px_rgba(255,67,112,0.2)] md:p-4">
                  <div
                    className="pointer-events-none absolute -right-10 -top-10 h-28 w-28 rounded-full opacity-[0.09]"
                    style={{ background: "radial-gradient(circle, #ff4370 0%, transparent 70%)" }}
                    aria-hidden
                  />
                  <div className="relative space-y-2 text-center md:space-y-2 md:text-left">
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-pink/25 bg-pink/[0.08] px-2.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.14em] text-pink">
                      <img
                        src="/star-sparkle.png"
                        alt=""
                        width={46}
                        height={43}
                        className="h-2.5 w-2.5 shrink-0 object-contain md:h-3 md:w-3"
                        aria-hidden
                      />
                      Expédition rapide
                    </span>
                    <h2 className="text-base font-bold tracking-tight md:text-lg" style={{ color: "#ff4370" }}>
                      Obtenez-le aujourd&apos;hui
                    </h2>
                    <p className="text-[10px] leading-snug text-secondary md:text-[11px]">
                      Livraison offerte au-delà du seuil · Équipe disponible tous les jours.
                    </p>
                  </div>
                  <div className="relative mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <div className="flex gap-2 rounded-lg border border-pink/12 bg-white/90 p-2 shadow-sm transition-shadow hover:border-pink/25">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-pink/10">
                        <div className="icon-delivery-truck text-xl text-pink"></div>
                      </div>
                      <div className="min-w-0 text-left">
                        <div className="text-[11px] font-semibold leading-tight text-gray-900">Livraison gratuite</div>
                        <p className="mt-0.5 text-[9px] leading-tight text-secondary line-clamp-3">
                          Offerte dès <span className="font-medium text-gray-800">750 MAD</span>.
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 rounded-lg border border-pink/12 bg-white/90 p-2 shadow-sm transition-shadow hover:border-pink/25">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-pink/10">
                        <div className="icon-phone-call text-xl text-pink"></div>
                      </div>
                      <div className="min-w-0 text-left">
                        <div className="text-[11px] font-semibold leading-tight text-gray-900">Support</div>
                        <p className="mt-0.5 text-[9px] leading-tight text-secondary line-clamp-3">
                          Tel. & WhatsApp <span className="font-medium text-gray-800">8h30–22h</span>.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex w-full min-w-0 flex-col md:h-full md:w-1/2 md:max-w-none md:flex-none lg:pl-[15px] md:pl-2">
                <div className="list-payment flex min-h-0 w-full flex-1 flex-col">
                  <div className="relative flex h-full min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-pink/15 bg-gradient-to-br from-white via-[#fefcfd] to-[#f7f5f6] px-4 py-4 shadow-[0_8px_28px_-14px_rgba(255,67,112,0.18)] md:px-5 md:py-5">
                    <div
                      className="pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full opacity-[0.12]"
                      style={{ background: "radial-gradient(circle, #ff4370 0%, transparent 72%)" }}
                      aria-hidden
                    />
                    <div className="relative flex min-h-0 flex-1 flex-col justify-between gap-3">
                      <div className="flex flex-col gap-3">
                        <div className="flex flex-wrap items-center justify-center gap-2">
                          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200/80 bg-emerald-50/90 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-emerald-800">
                            <Icon.ShieldCheck size={12} weight="fill" className="text-emerald-600" />
                            SSL
                          </span>
                          <span className="inline-flex items-center gap-1.5 rounded-full border border-pink/25 bg-white px-3 py-1 text-[11px] font-semibold tracking-tight text-pink shadow-sm">
                            <img
                              src="/star-sparkle.png"
                              alt=""
                              width={46}
                              height={43}
                              className="h-3.5 w-3.5 shrink-0 object-contain"
                              aria-hidden
                            />
                            Paiement sécurisé garanti
                          </span>
                        </div>
                        <p className="text-center text-[10px] leading-snug text-secondary">
                          Cartes, livraison · données chiffrées
                        </p>
                      </div>
                      <div className="grid grid-cols-5 gap-1.5 sm:gap-2 md:mt-auto">
                        {[
                          "/payment/Frame-0.png",
                          "/payment/Frame-1.png",
                          "/payment/Frame-2.png",
                          "/payment/Frame-3.png",
                          "/payment/Frame-4.png",
                        ].map((src, idx) => (
                          <div
                            key={src}
                            className="flex min-h-[32px] items-center justify-center rounded-lg border border-line bg-white/95 p-1.5 shadow-[0_2px_8px_-2px_rgba(255,67,112,0.1)] transition-colors hover:border-pink/30"
                          >
                            <img
                              src={src}
                              width={500}
                              height={450}
                              alt={`Moyen de paiement ${idx + 1}`}
                              className="h-auto w-full max-h-6 object-contain sm:max-h-7"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="fx-reveal desc-tab md:pb-20 pb-10">
          <div className="container">
            <div className="flex items-center justify-center w-full">
              <div className="menu-tab flex items-center md:gap-[60px] gap-8">
                    </div>
                  </div>
            <div className="desc-block mt-8">
              <ProductSectionTitleStar
                badge="Fiche produit"
                title="Plus de détails sur le produit"
                subtitle="Matière, finitions et description complète pour vous aider à choisir la pièce qui vous correspond."
              />
              <div
                className={`desc-item description ${activeTab === "description" ? "open" : ""}`}>
                <div className="w-full">
                    <div className="bg-[#f7f7f7] rounded-2xl p-6 h-full flex flex-col">
                      <div className="mb-4 flex items-center gap-2">
                        <img
                          src="/star-sparkle.png"
                          alt=""
                          width={46}
                          height={43}
                          className="h-[22px] w-[22px] shrink-0 object-contain"
                          aria-hidden
                        />
                        <div className="heading6 text-pink m-0">Description</div>
                      </div>
                      <div className="text-secondary mt-2 leading-relaxed">
                      {productMain?.description}
                    </div>
                  </div>
                </div>
                <div className="grid lg:grid-cols-4 grid-cols-2 gap-[30px] md:mt-10 mt-6">
                  {/* Feature: Expédition rapide */}
                  <div className="flex flex-col items-center text-center">
                    <div className="w-20 h-20 md:w-28 md:h-28 rounded-full bg-[#f7f7f7] shadow-md flex items-center justify-center mb-4">
                      <div className="icon-delivery-truck text-pink text-4xl md:text-5xl"></div>
                    </div>
                    <div className="heading6 text-pink font-bold mb-2">Expédition rapide</div>
                    <div className="text-secondary text-sm">
                      Profitez d'une livraison rapide et fiable
                  </div>
                    </div>
                  {/* Feature: Matériau premium */}
                  <div className="flex flex-col items-center text-center">
                    <div className="w-20 h-20 md:w-28 md:h-28 rounded-full bg-[#f7f7f7] shadow-md flex items-center justify-center mb-4">
                      <div className="icon-cotton text-pink text-4xl md:text-5xl"></div>
                  </div>
                    <div className="heading6 text-pink font-bold mb-2">Matériau premium</div>
                    <div className="text-secondary text-sm">
                      Notre produit est fabriqué avec des matériaux soigneusement sélectionnés
                    </div>
                  </div>
                  {/* Feature: Haute qualité */}
                  <div className="flex flex-col items-center text-center">
                    <div className="w-20 h-20 md:w-28 md:h-28 rounded-full bg-[#f7f7f7] shadow-md flex items-center justify-center mb-4">
                      <div className="icon-guarantee text-pink text-4xl md:text-5xl"></div>
                    </div>
                    <div className="heading6 text-pink font-bold mb-2">Haute qualité</div>
                    <div className="text-secondary text-sm">
                      Un produit d'excellence qui répond aux plus hauts standards
                    </div>
                  </div>
                  {/* Feature: Très compatible */}
                  <div className="flex flex-col items-center text-center">
                    <div className="w-20 h-20 md:w-28 md:h-28 rounded-full bg-[#f7f7f7] shadow-md flex items-center justify-center mb-4">
                      <div className="icon-leaves-compatible text-pink text-4xl md:text-5xl"></div>
                    </div>
                    <div className="heading6 text-pink font-bold mb-2">Très compatible</div>
                    <div className="text-secondary text-sm">
                      S'adapte facilement à vos besoins et environnements variés.
                    </div>
                  </div>
                </div>
                
                <ProductSectionTitleStar
                  badge="Suggestions"
                  title="Vous allez adorer cela aussi"
                  subtitle="Une sélection de pièces qui complètent votre style — parfaites à associer avec ce produit."
                  className="my-10 md:my-12"
                />

                {/* Carrousel associé */}
                <div className="border-t border-line pt-8 mt-8">
                  <div className="w-full">
                    <Swiper
                      spaceBetween={20}
                      slidesPerView={2}
                      scrollbar={{
                        hide: false,
                      }}
                      modules={[Navigation, Scrollbar]}
                      breakpoints={{
                        576: {slidesPerView: 3, spaceBetween: 20},
                        768: { slidesPerView: 4, spaceBetween: 20},
                        1024: { slidesPerView: 5, spaceBetween: 20},
                        1290: {slidesPerView: 6, spaceBetween: 20}}}className="pb-4">
                      {data
                        ?.filter((item) => item?.action !== "achat rapide")
                        ?.slice(0, 10)
                        ?.map((product) => (
                          <SwiperSlide key={product.id}>
                            <Product data={product} type="grid" />
                          </SwiperSlide>
                        ))}
                    </Swiper>
                  </div>
                </div>
              </div>
              <div
                className={`desc-item specifications flex items-center justify-center ${
                  activeTab === "specifications" ? "open" : ""
                }`}
              >
                <div className="lg:w-1/2 sm:w-3/4 w-full">
                  <div className="item bg-surface flex items-center gap-8 py-3 px-10">
                    <div className="text-title sm:w-1/4 w-1/3">Évaluation</div>
                    <div className="flex items-center gap-1">
                      <Rate currentRate={4} size={12} />
                      <p>(1.234)</p>
                    </div>
                  </div>
                  <div className="item flex items-center gap-8 py-3 px-10">
                    <div className="text-title sm:w-1/4 w-1/3">
                      Coque externe
                    </div>
                    <p>100% polyester</p>
                  </div>
                  <div className="item bg-surface flex items-center gap-8 py-3 px-10">
                    <div className="text-title sm:w-1/4 w-1/3">Doublure</div>
                    <p>100% polyuréthane</p>
                  </div>
                  <div className="item flex items-center gap-8 py-3 px-10">
                    <div className="text-title sm:w-1/4 w-1/3">Taille</div>
                    <p>S, M, L, XL</p>
                  </div>
                  <div className="item bg-surface flex items-center gap-8 py-3 px-10">
                    <div className="text-title sm:w-1/4 w-1/3">Couleurs</div>
                    <p>Gris, Rouge, Bleu, Noir</p>
                  </div>
                  <div className="item flex items-center gap-8 py-3 px-10">
                    <div className="text-title sm:w-1/4 w-1/3">Soin</div>
                    <div className="flex items-center gap-5"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={`popup-img ${openPopupImg ? "open" : ""}`}>
          <span
            className="close-popup-btn absolute top-4 right-4 z-[2] cursor-pointer"
            onClick={() => {setOpenPopupImg(false)}}>
            <Icon.X className="text-3xl text-white" />
          </span>
          <Swiper
            spaceBetween={0}
            slidesPerView={1}
            modules={[Navigation, Thumbs]}
            navigation={true}
            loop={shouldEnableLoop(productImages.length, 1, 1)}
            className="popupSwiper"
            onSwiper={(swiper: any) => {
              swiperRef.current = swiper;
            }}>
            {productImages.map((image, index) => (
              <SwiperSlide key={index} onClick={() => { setOpenPopupImg(false)}}>
                <img
                  src={image}
                  width={1000}
                  height={1000}
                  
                  alt="image-prd"
                  className="w-full aspect-[3/4] object-contain rounded-xl"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  onError={(e) => {
                    e.currentTarget.src = "/default.png"; 
                  }}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
      <div
        className={`fixed md:hidden bottom-3 w-full flex flex-col gap-3 px-4 transition-all duration-200 ${
          isModalOpen || showBuyNowModal ? "opacity-0 pointer-events-none translate-y-3" : "opacity-100 translate-y-0"
        }`}
        style={{ zIndex: 200 }}
      >
        {/* Mobile sticky: précommande WhatsApp when rupture, sinon achat */}
        {outOfStock ? (
          <>
            <button
              type="button"
              onClick={handlePreorderViaWhatsApp}
              className="button-main w-full text-center transition-all duration-200 py-3.5 bg-[#25D366] hover:bg-[#20bd5a] text-white text-base font-semibold flex items-center justify-center gap-2 rounded-xl shadow-lg shadow-emerald-900/20"
            >
              <Icon.WhatsappLogo size={22} weight="fill" />
              Précommander sur WhatsApp
            </button>
            <p className="text-center text-[11px] font-medium text-neutral-500 px-1">
              Produit en rupture — nous vous confirmons délai & disponibilité sur WhatsApp
            </p>
            <button
              type="button"
              onClick={handlePoserUneQuestion}
              className="button-main w-full text-center transition-all duration-200 py-2.5 bg-white border-2 border-green-500/40 text-green-700 hover:bg-green-50 text-sm flex items-center justify-center gap-2 rounded-lg"
            >
              <Icon.WhatsappLogo size={18} />
              Autre question
            </button>
          </>
        ) : (
          <>
        <button
          type="button"
          onClick={handlePoserUneQuestion}
          className="button-main w-full text-center transition-all duration-200 py-3 bg-green-500 hover:bg-green-600 text-white text-lg flex items-center justify-center gap-2 rounded-lg shadow-lg"
        >
          <Icon.WhatsappLogo size={20} />
          Poser une question
        </button>
        
        {/* Achetez-le maintenant */}
          <div
            onClick={handleBuyNowClick}
            className="button-main w-full text-center transition-all duration-200 py-3 bg-pink hover:bg-greenprimary hover:text-white text-lg flex items-center justify-center gap-2 rounded-lg shadow-lg attention-button relative overflow-hidden group"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-pink via-pink-500 to-pink-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="absolute inset-0 shimmer-effect bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
            <div className="relative z-10 flex items-center gap-2">
              <Icon.CreditCard size={20} className="animate-bounce" />
              <span className="font-semibold">Achetez-le maintenant</span>
            </div>
          </div>
          </>
        )}
      </div>

      {showBuyNowModal && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 px-4"
          onClick={() => setShowBuyNowModal(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="buy-now-modal-title"
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="buy-now-modal-title" className="text-center text-lg font-bold text-gray-900">
              Que souhaitez-vous faire ?
            </h2>
            <p className="mt-2 text-center text-sm text-secondary">
              Votre article sera ajouté au panier.
            </p>
            <div className="mt-6 flex flex-col gap-3">
              <button
                type="button"
                onClick={handleBuyNowPlaceOrder}
                className="button-main w-full flex items-center justify-center gap-2 bg-pink py-3 text-center"
              >
                <Icon.CreditCard size={20} />
                Passer la commande
              </button>
              <button
                type="button"
                onClick={handleBuyNowContinueShopping}
                className="button-main w-full flex items-center justify-center gap-2 border-2 border-pink bg-white py-3 text-center text-pink hover:bg-pink/[0.08]"
              >
                <Icon.ShoppingBag size={20} />
                Continuer vos achats
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Enhanced Zoom Modal */}
      {zoomOpen && (
        <div
          ref={zoomBoxRef}
          className="fixed inset-0 z-[9999] bg-black bg-opacity-75 flex items-center justify-center cursor-grab"
          onMouseMove={handleZoomMouseMove}
          onMouseUp={handleZoomMouseUp}
          onMouseLeave={handleZoomMouseUp}
          onWheel={handleWheel}
          onClick={() => setZoomOpen(false)}
        >
          <div className="relative bg-white rounded-xl shadow-2xl border border-gray-200 max-w-[95vw] max-h-[95vh] overflow-hidden">
            {/* Header with controls */}
            <div className="absolute top-0 left-0 right-0 z-[10000] bg-white bg-opacity-95 backdrop-blur-sm border-b border-gray-200 px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                {/* Zoom Controls */}
                <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={e => { e.stopPropagation(); handleZoomOut(); }}
                    disabled={zoomLevel === 100}
                    className={`p-2 rounded-md transition-all duration-200 ${
                      zoomLevel === 100 
                        ? 'text-gray-400 cursor-not-allowed' 
                        : 'text-gray-700 hover:bg-white hover:shadow-sm'
                    }`}
                    title="Zoom Out"
                  >
                    <Icon.MagnifyingGlassMinus size={16} />
                  </button>
                  
                  <span className="px-2 py-1 text-sm font-medium text-gray-700 min-w-[60px] text-center">
                    {zoomLevel}%
                  </span>
                  
                  <button
                    onClick={e => { e.stopPropagation(); handleZoomIn(); }}
                    disabled={zoomLevel === 250}
                    className={`p-2 rounded-md transition-all duration-200 ${
                      zoomLevel === 250 
                        ? 'text-gray-400 cursor-not-allowed' 
                        : 'text-gray-700 hover:bg-white hover:shadow-sm'
                    }`}
                    title="Zoom In"
                  >
                    <Icon.MagnifyingGlassPlus size={16} />
                  </button>
                </div>
                
                {/* Zoom Level Buttons */}
                <div className="flex items-center gap-1">
                  {[100, 150, 200, 250].map((level) => (
                    <button
                      key={level}
                      onClick={e => { e.stopPropagation(); setZoomLevel(level); setZoomPos({ x: 0, y: 0 }); }}
                      className={`px-3 py-1 text-xs font-medium rounded-md transition-all duration-200 ${
                        zoomLevel === level
                          ? 'bg-pink text-white shadow-sm'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {level}%
                    </button>
                  ))}
                </div>
                
                {/* Reset Button */}
                <button
                  onClick={e => { e.stopPropagation(); handleZoomReset(); }}
                  className="px-3 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-all duration-200"
                  title="Reset Zoom"
                >
                  Reset
                </button>
              </div>
              
              {/* Close Button */}
              <button
                className="text-gray-600 hover:text-gray-800 bg-white bg-opacity-90 rounded-full p-2 shadow-md hover:bg-opacity-100 transition-all duration-200"
                onClick={e => { e.stopPropagation(); setZoomOpen(false); }}
                title="Close"
              >
                <Icon.X size={20} />
              </button>
            </div>
            
            {/* Image Container */}
            <div className="p-6 pt-20">
              <div className="relative overflow-hidden rounded-lg bg-gray-50">
                {mainImageSrc ? (
                  <img
                    ref={zoomImgRef}
                    src={mainImageSrc}
                    alt="zoomed"
                    className="max-h-[calc(90vh-120px)] max-w-[calc(90vw-48px)] select-none rounded-lg"
                    style={{
                      transform: `scale(${zoomLevel / 100}) translate(${zoomPos.x / (zoomLevel / 100)}px, ${zoomPos.y / (zoomLevel / 100)}px)`,
                      cursor: dragging ? 'grabbing' : 'grab',
                      transition: dragging ? 'none' : 'transform 0.3s ease-out',
                      transformOrigin: 'center center',
                    }}
                    draggable={false}
                    onMouseDown={e => { e.stopPropagation(); handleZoomMouseDown(e); }}
                    onClick={e => e.stopPropagation()}
                    onError={(e) => {
                      e.currentTarget.src = "/default.png";
                    }}
                  />
                ) : (
                  <div className="flex min-h-[200px] items-center justify-center text-sm text-gray-500">
                    Chargement…
                  </div>
                )}
              </div>
            </div>
            
            {/* Instructions */}
            {showInstructions && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-[10000] bg-pink text-white text-xs px-4 py-3 rounded-lg shadow-lg transition-all duration-500">
                <div className="flex items-center gap-2">
                  <Icon.HandGrabbing size={14} />
                  <span>Glisser pour déplacer • Molette pour zoomer • Touches + / - • 1-4 pour les niveaux • ÉCHAP pour fermer</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
export default Default;

